import type { ParameterTimeSeriesChartResult } from "@/lib/api";

export type ChartTimeBounds = { min: number; max: number };

type ChartTimeRange = {
  timeStart: Date;
  timeEnd: Date;
};

/**
 * Shared x-axis bounds for a set of charts: the extent of actual (non-null) data
 * across every result, intersected with the selected toolbar range. This crops the
 * empty leading/trailing bands (e.g. the "+8h" tail when observations have no
 * forecast) while keeping every chart on the exact same timeline.
 *
 * Falls back to the full toolbar range when there is no usable in-range data; the
 * caller's no-data overlay handles the truly-empty case.
 */
export function getSharedChartTimeBounds(
  charts: ParameterTimeSeriesChartResult[],
  timeRange: ChartTimeRange,
): ChartTimeBounds {
  const rangeStart = +timeRange.timeStart;
  const rangeEnd = +timeRange.timeEnd;
  const fallback: ChartTimeBounds = { min: rangeStart, max: rangeEnd };

  let dataMin = Infinity;
  let dataMax = -Infinity;

  for (const chart of charts) {
    for (const datapoint of chart.data?.datapoints ?? []) {
      if (datapoint.value === null || datapoint.value === undefined) continue;
      const timestamp = +new Date(datapoint.timestamp);
      if (Number.isNaN(timestamp)) continue;
      if (timestamp < dataMin) dataMin = timestamp;
      if (timestamp > dataMax) dataMax = timestamp;
    }
  }

  if (dataMin === Infinity || dataMax === -Infinity) return fallback;

  const min = Math.max(dataMin, rangeStart);
  const max = Math.min(dataMax, rangeEnd);

  if (min >= max) return fallback;

  return { min, max };
}
